<template>
  <div id="user-retention-modal" ref="modal" class="modal user-retention-modal" role="dialog">
    <div class="user-retention-modal__modal-box modal-dialog">
      <div class="user-retention-modal__modal-header" role="heading" tabIndex="0">
        <h3 class="user-retention-modal__modal-title">Design not perfect? We can help!</h3>

        <button
          class="user-retention-modal__modal-button-close"
          type="button"
          data-testid="close-user-retention-modal-button"
          @click="closeModalAndProcessAction"
        >
          <UseSvg icon="modal-close-big" />
        </button>
      </div>
      <div class="user-retention-modal__modal-body">
        <div class="user-retention-modal__info-container">
          <div class="user-retention-modal__info-container-before">
            <h5 class="user-retention-modal__info-container-bold-text">Original Upload</h5>
            <img
              class="user-retention-modal__image"
              :src="DTF_TRANSFER_USER_RETENTION_IMAGES.BEFORE_IMAGE_URL"
              alt="Before"
            />
            <div class="user-retention-modal__info-container-before-text">
              <h5 class="user-retention-modal__info-container-bold-text">
                Sample message to designer:
              </h5>
              <p class="user-retention-modal__info-container-description">
                Please remove the pink background, change the ribbon color to blue, remove freckles.
                Change “It's a girl!” to Emma.
              </p>
            </div>
          </div>
          <div class="user-retention-modal__info-container-arrow-button">
            <ArrowRightStemmedIcon />
          </div>
          <div class="user-retention-modal__info-container-after">
            <h5 class="user-retention-modal__info-container-bold-text">Designer Perfected</h5>
            <img
              class="user-retention-modal__image"
              :src="DTF_TRANSFER_USER_RETENTION_IMAGES.AFTER_IMAGE_URL"
              alt="After"
            />
            <div class="user-retention-modal__info-container-changes-list">
              <div
                v-for="(change, index) in DTF_TRANSFER_USER_RETENTION_CHANGES"
                :key="change"
                class="user-retention-modal__info-container-changes-list-item"
              >
                <span class="user-retention-modal__info-container-change-number">
                  {{ index + 1 }}
                </span>
                <h5 class="user-retention-modal__info-container-bold-text">{{ change }}</h5>
              </div>
            </div>
          </div>
        </div>
        <div class="user-retention-modal__action-btns-container">
          <button
            type="button"
            class="user-retention-modal__action-btn"
            data-testid="request-artist-edits-for-free"
            @click="handleRequestEditsButtonClick($event)"
          >
            {{ isPhone() ? "Use Free Artist Edit" : "Request Artist Edits for Free" }}
          </button>

          <button
            type="button"
            class="user-retention-modal__action-btn user-retention-modal__action-btn--secondary"
            data-testid="continue-button"
            @click="handleContinueButtonClick"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import ArrowRightStemmedIcon from "common/jiffy/icons/arrow-right-stemmed.svg";
import { isPhone } from "common/utils/browser";
import { scrollToTextAreaAndHighlight } from "components/jiffy/vue-product-details-dtf/common/dtfUserRetentionHelper";
import { track } from "jiffy-analytics";
import { ref, watch } from "vue";
import UseSvg from "../../vue-cart/UseSvg.vue";
import {
  DTF_TRANSFER_USER_RETENTION_CHANGES,
  DTF_TRANSFER_USER_RETENTION_IMAGES,
  MODAL_TRIGGER_POINTS
} from "./constants";

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  isMultiArtworkModalOpen: {
    type: Boolean,
    default: false
  },
  triggerPoint: {
    type: String,
    required: true
  }
});

const emit = defineEmits(["update:is-open"]);

const modal = ref(null);

const closeModalAndProcessAction = () => {
  emit("update:is-open", false);
  track("transfers_exit_intent_modal_ignored", {
    reason: props.triggerPoint
  });
  if (props.triggerPoint === MODAL_TRIGGER_POINTS.BACK_BUTTON) {
    window.history.go(-1);
  }
};

const onShow = () => {
  const $modalElement = $(modal.value);
  $modalElement
    .modal("show")
    .on("hide.bs.modal", () => {
      if (props.isOpen) {
        closeModalAndProcessAction();
      }
    })
    .on("hidden.bs.modal", () => {
      $modalElement.off("hide.bs.modal hidden.bs.modal");
      document.body.classList.remove("dtf-user-retention-modal-open");
    });
  document.body.classList.add("dtf-user-retention-modal-open");
};

const onHide = () => {
  const $modalElement = $(modal.value);
  $modalElement.modal("hide");
  document.body.classList.remove("dtf-user-retention-modal-open");
};

watch(
  () => props.isOpen,
  newValue => {
    if (newValue) {
      onShow();
    } else {
      onHide();
    }
  }
);

const handleRequestEditsButtonClick = event => {
  event.preventDefault();
  event.stopPropagation();
  emit("update:is-open", false);
  scrollToTextAreaAndHighlight(props.isMultiArtworkModalOpen ? 1 : 0);
  track("transfers_exit_intent_modal_recaptured", {
    reason: props.triggerPoint
  });
};

const handleContinueButtonClick = () => {
  closeModalAndProcessAction();
};

watch(
  () => props.triggerPoint,
  newValue => {
    if (newValue !== MODAL_TRIGGER_POINTS.NONE) {
      track("exit_intent_modal_load", {
        reason: newValue
      });
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/modal.scss";

.user-retention-modal {
  @include crafterpay-modal;

  &.modal {
    z-index: 9999;
  }

  &__modal-box {
    max-width: fit-content;

    &.modal-dialog {
      transform: none !important;
      @include with-tablet {
        transform: translateY(-50%) !important;
      }
    }

    @include with-tablet {
      top: 50%;
    }
  }

  &__modal-header {
    justify-content: space-between;
    align-items: center;
    border: 0;
    padding: 12px 8px 0px 8px;
    @include with-tablet {
      padding: 28px 32px 0px 32px;
    }
  }

  &__modal-title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 23px;

    @include with-tablet {
      font-size: 24px;
    }
  }

  &__modal-button-close {
    padding: 0;
    cursor: pointer;
    top: 0;
    height: 20px;
    width: 20px;
    position: relative;
    @include with-tablet {
      height: 24px;
      width: 24px;
    }
  }

  &__modal-body {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 8px;
    @include with-tablet {
      padding: 24px 32px;
    }
  }

  &__info-container {
    display: flex;
    padding: 12px 0px;
    background-color: $gray-body;
    border-radius: 8px;
    border-radius: 12px;
    border: 3px solid $default-gray-200;
    background: $white;
    max-width: 100%;
    justify-content: center;
    @include with-tablet {
      padding: 16px;
      max-width: 700px;
    }
  }

  &__info-container-before-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    @include with-tablet {
      gap: 8px;
    }
  }

  &__info-container-before,
  &__info-container-after {
    width: 40%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
    @include with-tablet {
      gap: 16px;
    }
  }

  &__info-container-bold-text {
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    color: $crafterpay-gray-modern-800;
    @include with-tablet {
      font-size: 14px;
      line-height: 20px;
    }
  }

  &__info-container-description {
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    color: $gray-modern-600;
    @include with-tablet {
      font-size: 14px;
      line-height: 16px;
    }
  }

  &__info-container-arrow-button {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $black;
    color: $white;
    border-radius: 50%;
    margin-top: 15%;
    @include with-tablet {
      width: 48px;
      height: 48px;
      align-self: flex-start;
    }
  }

  &__action-btns-container {
    display: flex;
    gap: 12px;
  }

  &__action-btn {
    width: 65%;
    padding-top: 16px;
    padding-bottom: 16px;
    text-align: center;
    background: $persian-blue;
    color: $white;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    &:hover {
      background: $dodger-blue;
    }
    &--secondary {
      border: 1px solid $gray-border;
      width: 35%;
      background: $white;
      color: $neutral-950;
      &:hover {
        background: $gray-50;
      }
    }
    &:disabled,
    &:hover:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    border-radius: 14px;
    @include with-tablet {
      border-radius: 3px;
    }
  }

  &__image {
    height: 150px;
    width: 150px;
    object-fit: contain;
    @include with-tablet {
      height: 200px;
      width: 200px;
    }
  }

  &__info-container-changes-list {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  &__info-container-changes-list-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 4px;
  }

  &__info-container-change-number {
    border-radius: 50%;
    background-color: $green-emerald-small-light;
    color: $white;
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
  }
}
</style>

<style lang="scss">
.dtf-user-retention-modal-open {
  overflow: hidden;
}
</style>
